import axios from 'axios';

// Build an absolute URL from a port-prefixed suffix like `:9000/api`. In a
// Caddy-routed worktree (`<svc>.<slug>.localhost`) the suffix's port selects
// the target service hostname so traffic flows backend→backend, model→model.
// Outside that pattern we fall back to the original same-host concatenation,
// which keeps legacy `127.0.0.1:<port>` dev working.
const protocol = window.location.protocol;
const hostname = window.location.hostname;
const worktreeMatch = hostname.match(/^[a-z0-9-]+\.([a-z0-9-]+)\.localhost$/);
const portToService = { 9000: 'backend', 5001: 'model' };

function rootFor(suffix) {
    if (!suffix) return '';
    const m = suffix.match(/^:(\d+)(.*)$/);
    if (worktreeMatch && m) {
        const [, port, path] = m;
        const service = portToService[port] || 'backend';
        return `${protocol}//${service}.${worktreeMatch[1]}.localhost${path}`;
    }
    return `${protocol}//${hostname}${suffix}`;
}

// API_ROOT is the core backend mount — every authenticated call (auth/token
// refresh, user, onboardings, configuration, scenarios) resolves against it.
// If VITE_API_SUFFIX is absent from the build env, rootFor() returns '' and
// every request targets the site root, where nginx's SPA catch-all
// (`try_files $uri /index.html`) answers instead of Django: GETs get the
// index.html document (which then crashes `.map`/JSON consumers) and POSTs get
// a 405 — a site-wide outage that also logs users out. This bit prod when a
// stale (pre-Vite) VM `.env` left the var unset. Fall back to the conventional
// same-origin `/api` mount (also what the Vite dev proxy serves) so a missing
// suffix degrades to "correct" rather than "everything broken", and shout so
// the misconfig is caught in review/staging.
const apiSuffix = import.meta.env.VITE_API_SUFFIX || '/api';
if (!import.meta.env.VITE_API_SUFFIX) {
    console.error('[api] VITE_API_SUFFIX is unset — defaulting API_ROOT to "/api". Check the build environment.');
}
export const API_ROOT = rootFor(apiSuffix);
export const GRAT_V2_API_ROOT = rootFor(import.meta.env.VITE_GRAT_V2_API_SUFFIX);
export const CLAT_V2_API_ROOT = rootFor(import.meta.env.VITE_CLAT_V2_API_SUFFIX);
export const CRUT_V1_API_ROOT = rootFor(import.meta.env.VITE_CRUT_V1_API_SUFFIX);
export const CRUT_V2_API_ROOT = rootFor(import.meta.env.VITE_CRUT_V2_API_SUFFIX);
export const CAPITAL_GAINS_COMPARISON_API_ROOT = rootFor(import.meta.env.VITE_CAPITAL_GAINS_COMPARISON_API_SUFFIX);
export const ESTATE_COMPARISON_API_ROOT = rootFor(import.meta.env.VITE_ESTATE_COMPARISON_API_SUFFIX);
export const ESTATE_BURN_COMPARISON_API_ROOT = rootFor(
    import.meta.env.VITE_ESTATE_BURN_COMPARISON_API_SUFFIX ||
        `${import.meta.env.VITE_ESTATE_COMPARISON_API_SUFFIX}_burn`,
);
export const ORDINARY_INCOME_COMPARISON_API_ROOT = rootFor(import.meta.env.VITE_ORDINARY_INCOME_COMPARISON_API_SUFFIX);
export const IRA_API_ROOT = rootFor(import.meta.env.VITE_IRA_API_SUFFIX);
export const OIL_GAS_API_ROOT = rootFor(import.meta.env.VITE_OIL_GAS_API_SUFFIX);
export const PPLI_API_ROOT = rootFor(import.meta.env.VITE_PPLI_API_SUFFIX);
export const QSBS_API_ROOT = rootFor(import.meta.env.VITE_QSBS_API_SUFFIX);
export const SOLAR_API_ROOT = rootFor(import.meta.env.VITE_SOLAR_API_SUFFIX);
export const NGT_API_ROOT = rootFor(import.meta.env.VITE_NGT_API_SUFFIX);
export const MODEL_TEST_API_ROOT = rootFor(import.meta.env.VITE_MODEL_TEST_API_SUFFIX || ':5001/api/v1/test');

export const MODEL_API_ROOTS = [
    GRAT_V2_API_ROOT,
    CLAT_V2_API_ROOT,
    CRUT_V1_API_ROOT,
    CRUT_V2_API_ROOT,
    CAPITAL_GAINS_COMPARISON_API_ROOT,
    ESTATE_COMPARISON_API_ROOT,
    ESTATE_BURN_COMPARISON_API_ROOT,
    ORDINARY_INCOME_COMPARISON_API_ROOT,
    IRA_API_ROOT,
    OIL_GAS_API_ROOT,
    PPLI_API_ROOT,
    QSBS_API_ROOT,
    SOLAR_API_ROOT,
    NGT_API_ROOT,
].filter(Boolean);

export const addOrEditItem = (path, root_path = '') => {
    return async ({ dispatch, rootGetters }, payload = {}) => {
        // Clone: the steps below delete keys (user_id/id/uuid/action/…); never mutate
        // the caller's object (often reactive component state).
        payload = { ...payload };
        let method = 'post';
        // Users (and uuid-scoped models below) are addressed by uuid in API
        // paths; integer ids are no longer routable.
        let userId = rootGetters.user.uuid;
        if ('user_id' in payload) {
            userId = payload.user_id || userId;
            delete payload.user_id;
        }
        let additional_path = '';
        if (root_path !== '') {
            additional_path = `${root_path}${payload.additional_id}/`;
            delete payload.additional_id;
        }
        if (!userId) {
            const err = new Error('User ID is required for API calls');
            logApiError(err, dispatch);
            throw err;
        }
        let url = `${API_ROOT}/${userId}/${additional_path}${path}`;
        if (payload.id || payload.uuid) {
            method = 'patch';
            // uuid-routed models (onboardings, solar activities) carry both;
            // models still addressed by integer pk only carry `id`.
            url = `${url}${payload.uuid || payload.id}/`;
            delete payload.id;
            delete payload.uuid;
        }
        if (payload.action) {
            url = `${url}${payload.action}/`;
            delete payload.action;
        }
        try {
            const { data } = await axios[method](url, payload, rootGetters.axiosConfig);
            return data;
        } catch (e) {
            logApiError(e, dispatch);
            throw e;
        }
    };
};

export const deleteItem = (path, root_path = '', safe = false) => {
    return async ({ dispatch, rootGetters }, payload = {}) => {
        payload = { ...payload };
        const id = payload.uuid || payload.id;
        let userId = rootGetters.user.uuid;
        if (payload.user_id) {
            userId = payload.user_id;
            delete payload.user_id;
        }
        let additional_path = '';
        if (root_path !== '') {
            additional_path = `${root_path}${payload.additional_id}/`;
            delete payload.additional_id;
        }
        try {
            // allow deletion without id for cases like discard draft
            const id_part = id ? `${id}/` : '';
            const url = `${API_ROOT}/${userId}/${additional_path}${path}${id_part}`;
            if (safe) {
                await axios.patch(url, { is_deleted: true }, rootGetters.axiosConfig);
            } else {
                await axios.delete(url, rootGetters.axiosConfig);
            }

            return true;
        } catch (e) {
            logApiError(e, dispatch);
            return false;
        }
    };
};

export const getItems = (path, root_path = '') => {
    return async ({ dispatch, rootGetters }, payload = { context: {}, id: null, params: {} }) => {
        payload = { ...payload };
        let userId = rootGetters.user.uuid;
        if (payload.user_id) {
            userId = payload.user_id;
            delete payload.user_id;
        }
        const module = `${userId}`;
        const id = payload.uuid || payload.id;
        const params = payload.params || {};
        let p = path;
        let additional_path = '';
        if (root_path !== '') {
            additional_path = `${root_path}${payload.additional_id}/`;
            delete payload.additional_id;
        }
        if (id) {
            p = `${path}${id}/`;
        }
        const url = `${API_ROOT}/${module}/${additional_path}${p}`;
        const config = {
            ...rootGetters.axiosConfig,
            params,
        };
        try {
            const { data } = await axios.get(url, config);
            return data;
        } catch (e) {
            logApiError(e, dispatch);
            return false;
        }
    };
};

export const logApiError = (error, dispatch) => {
    if (error.response) {
        // Only an expired/invalid session (401) ends the session. A 403 is
        // authenticated-but-forbidden (an advisor touching a client they don't
        // manage, a per-object denial, a non-staff hitting an admin resource) and
        // MUST NOT tear down the session + impersonation state — it should surface
        // to the caller as a normal error instead.
        if (error.response.status === 401) {
            dispatch('logout', null, { root: true });
        } else if (error.response.status !== 409) {
            // Conflicts (409) are expected on some PUTs (version conflicts) and are
            // hidden from the user. Log only status + url — never the request/response
            // objects, which carry the Bearer token and SSN/password request bodies.
            console.debug('API error', error.response.status, error.config?.url);
        }
    } else if (error.request) {
        // The request was made but no response was received.
        console.debug('API error: no response', error.config?.url);
    } else {
        // Something happened setting up the request that triggered an Error.
        console.debug('API error: request setup', error.message);
    }
};
